// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { ticketSchema } from '@/ticket/api/types';
import { z } from 'zod';

const reopenTicketResponseSchema = z
  .object({
    data: ticketSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, ticket: data }));

type ReopenTicketResponse = z.infer<typeof reopenTicketResponseSchema>;

interface ReopenTicketParameters {
  reopenReason?: string;
}

/**
 * Reopens a closed ticket by its unique key and updates it with the provided parameters.
 *
 * @param {string} key - The unique identifier of the ticket to be reopened.
 * @param {ReopenTicketParameters} parameters - The parameters required to reopen the ticket, including relevant data for the operation.
 * @return {Promise<ReopenTicketResponse>} A promise that resolves to the response containing the updated ticket information after it has been reopened.
 */
export async function reopenTicket(
  key: string,
  parameters: ReopenTicketParameters
): Promise<ReopenTicketResponse> {
  const url = `/v1/ticket/${key}/reopen`;
  const response = await api.post(url, {
    ...parameters,
  });

  return parseResponse(reopenTicketResponseSchema, response.data, {
    schemaName: 'ReopenTicket',
    endpoint: url,
  });
}
